import React from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import "./TableUI.css";

/* ------------------------------------------------------------------ */
/* Shared table UI primitives (tooltips, icon actions, status switch). */
/* Every icon-only control in the admin tables goes through these so   */
/* placement, delay, sizing and colours stay consistent.               */
/* ------------------------------------------------------------------ */

const TOOLTIP_PROPS = {
  placement: "top",
  arrow: true,
  enterDelay: 300,
  enterNextDelay: 150,
  enterTouchDelay: 500,
  leaveTouchDelay: 1500,
};

/**
 * Consistent MUI tooltip. Disabled elements don't fire mouse events, so
 * pass `wrap` when the child can be disabled and it gets a span wrapper.
 */
const ActionTooltip = ({ title, children, wrap = false, placement }) => {
  if (!title) return children;
  return (
    <Tooltip
      title={title}
      {...TOOLTIP_PROPS}
      placement={placement || TOOLTIP_PROPS.placement}
    >
      {wrap ? <span className="tbl-tooltip-wrap">{children}</span> : children}
    </Tooltip>
  );
};

/**
 * Icon-only action button with tooltip + accessible label.
 * variant: "primary" | "neutral" | "danger" | "success"
 */
const IconAction = ({
  title,
  icon,
  onClick,
  variant = "neutral",
  disabled = false,
  className = "",
  size = "small",
  ...rest
}) => (
  <ActionTooltip title={title} wrap={disabled}>
    <IconButton
      type="button"
      size={size}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`tbl-icon-action tbl-icon-action--${variant} ${className}`.trim()}
      {...rest}
    >
      {icon}
    </IconButton>
  </ActionTooltip>
);

const plainText = (node) =>
  typeof node === "string" || typeof node === "number" ? String(node).trim() : "";

/* Tooltip copy that describes the current state and what a click will do. */
const switchTooltip = (checked, label, disabled) => {
  const text = plainText(label);
  const isStatusWord = /^(active|inactive)$/i.test(text);
  const current = isStatusWord || !text ? (checked ? "Active" : "Inactive") : text;

  if (disabled) return `${current} — cannot be changed`;
  if (isStatusWord || !text) {
    return checked
      ? "Active — click to deactivate"
      : "Inactive — click to activate";
  }
  return `${current} — click to ${checked ? "turn off" : "turn on"}`;
};

/**
 * MUI switch used in place of the Bootstrap form-switch in table rows.
 * `onChange` is the row's existing handler, passed through untouched;
 * hovering only shows the tooltip and never calls it.
 * `label` keeps whatever text the row used to show next to the switch;
 * it is announced to screen readers and summarised in the tooltip.
 */
const StatusSwitch = ({ checked, disabled, onChange, id, label, tooltip }) => {
  const isOn = !!checked;
  const text = plainText(label) || (isOn ? "Active" : "Inactive");
  return (
    <div className="tbl-status-switch">
      <ActionTooltip
        title={tooltip || switchTooltip(isOn, label, disabled)}
        wrap
      >
        <Switch
          size="small"
          id={id}
          checked={isOn}
          disabled={!!disabled}
          onChange={onChange}
          className="tbl-status-switch__control"
          inputProps={{ role: "switch", "aria-label": text }}
        />
      </ActionTooltip>
    </div>
  );
};

/**
 * Compact icon-only filter buttons used next to list/table search inputs.
 * `onClick` is the page's existing search / reset handler, passed through.
 */
const FilterIconButton = ({ title, icon, variant, onClick, className = "", ...rest }) => (
  <ActionTooltip title={title} wrap={!!rest.disabled}>
    <button
      type="button"
      aria-label={title}
      onClick={onClick}
      className={`btn tbl-filter-btn tbl-filter-btn--${variant} ${className}`.trim()}
      {...rest}
    >
      {icon}
    </button>
  </ActionTooltip>
);

const SearchButton = ({ title = "Search", ...props }) => (
  <FilterIconButton
    title={title}
    variant="search"
    icon={<SearchIcon className="tbl-filter-btn__icon" />}
    {...props}
  />
);

const ResetButton = ({ title = "Reset", ...props }) => (
  <FilterIconButton
    title={title}
    variant="reset"
    icon={<RestartAltIcon className="tbl-filter-btn__icon" />}
    {...props}
  />
);

/* Icon-only export / sample download. `title` names what gets downloaded. */
const DownloadButton = ({ title = "Download Excel", ...props }) => (
  <FilterIconButton
    title={title}
    variant="download"
    icon={<FileDownloadOutlinedIcon className="tbl-filter-btn__icon" />}
    {...props}
  />
);

export {
  ActionTooltip,
  IconAction,
  StatusSwitch,
  SearchButton,
  ResetButton,
  DownloadButton,
  TOOLTIP_PROPS,
};
